import { useAtomValue } from "jotai";
import type { FunctionComponentWithRef } from "react";
import { useEffect, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { useFallbackStorageValue } from "shared/hooks/fallback-storage-value";
import { theme } from "shared/lib/styles";
import { notEmpty } from "shared/lib/utils";

import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { useAppBarStickyHeightValue } from "scmp-app/components/app-bar/hooks";
import { AISearchTooltipLocalStorageKey } from "scmp-app/components/header/features/search-bar-tooltip/consts";
import { RefineSearchDrawer } from "scmp-app/components/search/search-result-panel/refine-search/drawer";
import { TopicsCardPortalElementId } from "scmp-app/components/search/search-result-panel/result-list/topics-card/consts";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import { Typeform } from "scmp-app/lib/typeform";
import type { ArticleSearchAnswerModeEnum } from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { searchResultStreamAtom } from "./atoms";
import type { SearchPageType } from "./contexts";
import { SearchContextProvider } from "./contexts";
import { SearchExploreTopics } from "./search-explore-topics";
import {
  Container,
  RefineSearchContainer,
  SearchResultPanelContainer,
  StyledAdSlot,
  StyledRefineSearchPanel,
  StyledSearchAIWidgetPlaceholder,
  StyledSearchExploreKeyAngles,
  StyledSearchFollowTheTimeline,
  StyledSearchInput,
  StyledSearchResultPanel,
  TopicsCardPortal,
} from "./styles";

type Props = {
  className?: string;
  embedMode?: boolean;
  embedWidgetOnly?: boolean;
  routeQueryValue: string;
  searchPageType?: SearchPageType;
  streamId: string;
};

export const Search: FunctionComponentWithRef<Props, HTMLDivElement> = ({
  className,
  embedMode = false,
  embedWidgetOnly = false,
  ref,
  routeQueryValue,
  searchPageType = "default",
  streamId,
}) => {
  const hasActiveSearch = notEmpty(routeQueryValue);
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const { mode } = useAtomValue(searchResultStreamAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const answerModeToBackend = useMemo<ArticleSearchAnswerModeEnum | undefined>(() => {
    if (asyncRosettaState?.status !== "success") return;
    const isAutoMode =
      (asyncRosettaState?.result?.hasScmpPlusAccessRight ?? false) ||
      (asyncRosettaState?.result?.isScmpPlusSubscriber ?? false) ||
      (asyncRosettaState?.result?.user?.isInternalStaff ?? false) ||
      false;
    return isAutoMode ? "AUTO" : "NAVIGATOR";
  }, [asyncRosettaState]);

  // close search tooltip
  const { set: setTooltipDismissed, value: isTooltipDismissed } = useFallbackStorageValue(
    AISearchTooltipLocalStorageKey,
    { defaultValue: "false" },
  );

  useEffect(() => {
    if (isTooltipDismissed === "false") setTooltipDismissed("true");
  }, [isTooltipDismissed, setTooltipDismissed]);

  const renderAIWidget = () => {
    if (embedMode || embedWidgetOnly) return <StyledSearchExploreKeyAngles />;
    switch (mode) {
      case "NAVIGATOR":
        return <StyledSearchExploreKeyAngles />;
      case "TIMELINE":
        return <StyledSearchFollowTheTimeline />;
      default:
        return <StyledSearchAIWidgetPlaceholder />;
    }
  };

  return (
    <SearchContextProvider
      currentValue={routeQueryValue}
      embedMode={embedMode}
      embedWidgetOnly={embedWidgetOnly}
      searchPageType={searchPageType}
    >
      <BaseLinkContextProvider linkTarget={embedMode ? "_blank" : undefined}>
        <Container $isEmbedMode={embedMode} className={className} ref={ref}>
          <SearchResultPanelContainer>
            {!embedWidgetOnly && <StyledSearchInput $stickyStackedHeight={appBarStickyHeight} />}

            <ClientSideSuspense>
              <ErrorBoundary fallback={<></>}>
                {hasActiveSearch ? <>{renderAIWidget()}</> : <SearchExploreTopics />}
                {answerModeToBackend && (
                  <StyledSearchResultPanel
                    answerMode={answerModeToBackend}
                    noResultList={embedWidgetOnly}
                    streamId={streamId}
                  />
                )}
              </ErrorBoundary>
            </ClientSideSuspense>
          </SearchResultPanelContainer>

          {!embedMode && hasActiveSearch && (
            <RefineSearchContainer>
              <StyledRefineSearchPanel />
              <TopicsCardPortal id={TopicsCardPortalElementId} />
              {searchPageType === "default" && (
                <StyledAdSlot
                  adUnit="d_lrec1"
                  breakpoint={theme.breakpoints.up("desktop")}
                  sizes={[
                    [300, 250],
                    [300, 600],
                  ]}
                  targeting={{ paid: TargetingPaidType.NotArticle }}
                  withLabel
                  withLabelBackground
                  withMaxContainerSize={false}
                  zone="search/home"
                />
              )}
            </RefineSearchContainer>
          )}
        </Container>
        <RefineSearchDrawer />
        {searchPageType === "default" && !embedMode && hasActiveSearch && (
          <Typeform configKey="SEARCH_RESULT" />
        )}
      </BaseLinkContextProvider>
    </SearchContextProvider>
  );
};

Search.displayName = "Search";
